import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { FormSelect } from '@/components/FormSelect';
import { FormTextarea } from '@/components/FormTextarea';
import { useToast } from '@/hooks/useToast';
import {
    HEALTH_PLAN_PROVIDERS,
    validateHealthPlanChoice,
    type HealthPlanAction,
    type HealthPlanProvider,
} from '../../../../../shared/dpRequests';
import { AttachmentPicker } from '../AttachmentPicker';
import { ChoiceCards } from '../ChoiceCards';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { useSubmitHealthPlan } from '../../hooks/data/useDpRequestMutations';
import { DP_REQUESTS_PATH } from '../../routes';
import { DependentChecklist } from './DependentChecklist';
import { HEALTH_ACTION_OPTIONS, LEAVE_SCOPE_OPTIONS, NEEDS_PLAN_MESSAGE } from './healthPlanForm.definitions';
import type { HealthPlanFormProps } from './healthPlanForm.types';

export function HealthPlanForm({ data }: HealthPlanFormProps) {
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const submit = useSubmitHealthPlan();
    const [uploading, setUploading] = useState(false);
    const { snapshot } = data;

    const [action, setAction] = useState<HealthPlanAction | ''>('');
    const [provider, setProvider] = useState<HealthPlanProvider | ''>('');
    const [leaveScope, setLeaveScope] = useState<'holder' | 'dependents' | ''>('');
    const [dependentNames, setDependentNames] = useState<string[]>([]);
    const [note, setNote] = useState('');
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [error, setError] = useState<string | null>(null);

    const hasPlan = snapshot.hasHealthInsurance;
    const covered = snapshot.dependents.filter(item => item.isCovered);
    const notCovered = snapshot.dependents.filter(item => !item.isCovered);

    const includeHolder = action === 'include' ? !hasPlan : action === 'leave' && leaveScope === 'holder';
    const showProvider = action === 'change' || (action === 'include' && !hasPlan);
    const showIncludeDependents = action === 'include';
    const showLeaveDependents = action === 'leave' && leaveScope === 'dependents';

    function selectAction(next: HealthPlanAction) {
        setAction(next);
        setProvider('');
        setLeaveScope('');
        setDependentNames([]);
        setError(null);
    }

    function toggle(name: string) {
        setDependentNames(current => (current.includes(name) ? current.filter(item => item !== name) : [...current, name]));
    }

    async function handleSubmit() {
        if (!action) {
            setError('Escolha o que você precisa');
            return;
        }
        if (action === 'leave' && !leaveScope) {
            setError('Escolha quem sai do plano');
            return;
        }
        const names = action === 'leave' && leaveScope === 'holder' ? [] : dependentNames;
        const problem = validateHealthPlanChoice(
            { action, provider: provider || null, includeHolder, dependentNames: names },
            snapshot
        );
        if (problem) {
            setError(problem);
            toast.error('Revise o pedido', problem);
            return;
        }

        setError(null);
        if (!user?.uid) return;
        setUploading(true);
        try {
            await withUploadedAttachments(files, user.uid, attachments =>
                submit.mutateAsync({
                    action,
                    provider: provider || null,
                    includeHolder,
                    dependentNames: names,
                    note: note.trim() || undefined,
                    attachments,
                })
            );
            navigate(DP_REQUESTS_PATH);
        } catch {
            return;
        } finally {
            setUploading(false);
        }
    }

    const busy = submit.isPending || uploading;
    const providerOptions = HEALTH_PLAN_PROVIDERS.filter(item => item !== snapshot.provider || action !== 'change').map(item => ({ value: item, label: item }));

    return (
        <div className="space-y-6">
            <section className="rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <h2 className="font-montserrat text-base font-semibold text-raro-gray-900">Sua situação hoje</h2>
                {hasPlan ? (
                    <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-3">
                        <div><dt className="text-xs font-semibold uppercase text-raro-gray-500">Operadora</dt><dd className="text-raro-gray-900">{snapshot.provider ?? '—'}</dd></div>
                        <div><dt className="text-xs font-semibold uppercase text-raro-gray-500">Desde</dt><dd className="text-raro-gray-900">{snapshot.inclusionDate ? snapshot.inclusionDate.split('-').reverse().join('/') : '—'}</dd></div>
                        <div><dt className="text-xs font-semibold uppercase text-raro-gray-500">Dependentes no plano</dt><dd className="text-raro-gray-900">{covered.length > 0 ? covered.map(item => item.name).join(', ') : 'Nenhum'}</dd></div>
                    </dl>
                ) : (
                    <p className="mt-2 text-sm text-raro-gray-600">Você não tem plano de saúde cadastrado.</p>
                )}
            </section>

            <section className="space-y-5 rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm sm:p-6">
                <ChoiceCards
                    legend="O que você precisa?"
                    name="healthAction"
                    value={action}
                    options={HEALTH_ACTION_OPTIONS.map(option => ({ ...option, disabled: option.value !== 'include' && !hasPlan }))}
                    onChange={selectAction}
                    hint={hasPlan ? undefined : NEEDS_PLAN_MESSAGE}
                    disabled={busy}
                />

                {action === 'leave' && (
                    <ChoiceCards legend="Quem sai?" name="leaveScope" value={leaveScope} options={LEAVE_SCOPE_OPTIONS} onChange={value => { setLeaveScope(value); setDependentNames([]); }} disabled={busy} />
                )}

                {showProvider && (
                    <FormSelect
                        label={action === 'change' ? 'Nova operadora' : 'Operadora'}
                        name="provider"
                        value={provider}
                        onChange={event => setProvider(event.target.value as HealthPlanProvider)}
                        options={providerOptions}
                        placeholder="Selecione"
                        disabled={busy}
                        required
                    />
                )}

                {showIncludeDependents && (
                    <DependentChecklist
                        legend={hasPlan ? 'Quem será incluído?' : 'Incluir dependentes também (opcional)'}
                        dependents={notCovered}
                        selected={dependentNames}
                        onToggle={toggle}
                        emptyText="Todos os dependentes cadastrados já estão no plano. Dependentes novos entram pela Atualização de dados."
                        disabled={busy}
                    />
                )}

                {showLeaveDependents && (
                    <DependentChecklist legend="Quem sai do plano?" dependents={covered} selected={dependentNames} onToggle={toggle} emptyText="Nenhum dependente no plano." disabled={busy} />
                )}

                <FormTextarea label="Observação (opcional)" name="note" value={note} onChange={event => setNote(event.target.value)} maxLength={500} showCharCount rows={2} disabled={busy} />

                <AttachmentPicker
                    files={files}
                    onAdd={file => setFiles(current => [...current, file])}
                    onRemove={index => setFiles(current => current.filter((_, position) => position !== index))}
                    hint="Opcional. Documentos que ajudem o DP, como certidões. O arquivo só é enviado quando você enviar o pedido."
                    disabled={busy}
                />

                {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}
            </section>

            <p className="text-xs text-raro-gray-500">O DP informa a data em que a mudança passa a valer e atualiza o seu cadastro ao concluir.</p>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button variant="ghost" onClick={() => navigate(DP_REQUESTS_PATH)} disabled={busy}>Cancelar</Button>
                <Button onClick={() => void handleSubmit()} loading={busy} disabled={!user?.uid || !action}>{uploading ? 'Enviando anexos…' : 'Enviar ao DP'}</Button>
            </div>
        </div>
    );
}
